import {
  Box,
  Button,
  List,
  ListItem,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery
} from '@mui/material'
import React, { useEffect } from 'react'
import {
  MobileLogoutWrapper,
  StyledSidebarCloseBtn,
  StyledSidebarNavLink,
  sidebarBoxStyles,
  styledNavIconBox,
  styledNavList,
  styledSidebarNavIcon
} from './styles'
import { directorRoutes, residenceRoutes } from 'Router/sidebarRoutes'
import { useDashboard } from 'store/DashboardContext'
import { ChevronLeft, LogOut } from 'lucide-react'
import useAuthStore from 'store/userStore'
import { useQueryClient } from '@tanstack/react-query'
import { useAuthenticator } from '@aws-amplify/ui-react'
import { UserRoles } from 'types/enum/enum'

export const Sidebar: React.FC = () => {
  const {
    state: { isSideNavOpen },
    actions: { toggleSideNav }
  } = useDashboard()
  const { signOut } = useAuthenticator()
  const { user, logout } = useAuthStore()
  const queryClient = useQueryClient()
  const mobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'))

  const routes = user?.roles.includes(UserRoles.DIRECTOR)
    ? directorRoutes
    : residenceRoutes

  const handleLogout = async () => {
    if (mobile) {
      toggleSideNav()
    }

    try {
      // Clear the Amplify session
      await signOut()

      // Clear local state and caches
      logout() // Clear user from your auth store
      queryClient.clear() // Clear React Query cache
      localStorage.clear() // Clear local storage to avoid stale data

      // Redirect to the login page to ensure a clean state
      window.location.href = '/login'
    } catch (error) {
      console.error('Error during logout:', error)
    }
  }

  useEffect(() => {
    if (mobile && !isSideNavOpen) {
      toggleSideNav()
    }
  }, [])

  return (
    <Box sx={(theme) => sidebarBoxStyles({ theme, isSideNavOpen })}>
      {!mobile && (
        <Box position={'relative'} width={'100%'} overflow={'visible'}>
          <StyledSidebarCloseBtn>
            <Button
              sx={(theme) => ({
                padding: 0,
                minWidth: 0,
                color: theme.palette.white.main
              })}
              onClick={() => {
                toggleSideNav()
              }}
            >
              {!isSideNavOpen ? (
                <ChevronLeft />
              ) : (
                <ChevronLeft style={{ transform: 'rotate(180deg)' }} />
              )}
            </Button>
          </StyledSidebarCloseBtn>
        </Box>
      )}
      <Box
        sx={{
          overflowY: 'auto',
          height: '100%',
          ['&::-webkit-scrollbar']: {
            display: 'none'
          }
        }}
      >
        <List sx={(theme) => styledNavList(theme, isSideNavOpen)}>
          {routes.map((route) => (
            <ListItem
              key={route.path}
              sx={{
                padding: 0
              }}
            >
              <StyledSidebarNavLink
                to={route.path}
                exact
                onClick={mobile ? toggleSideNav : undefined}
              >
                {!mobile ? (
                  <>
                    <Box
                      sx={(theme) => styledNavIconBox(theme, isSideNavOpen)}
                      component={'span'}
                    >
                      <route.icon size={34} />
                    </Box>
                    <Typography
                      variant="caption2_regular"
                      sx={(theme) => styledSidebarNavIcon(theme, isSideNavOpen)}
                    >
                      {route.name}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Box
                      sx={(theme) => {
                        return {
                          marginRight: theme.spacing(1),
                          display: 'inline-flex'
                        }
                      }}
                      component={'span'}
                    >
                      <route.icon />
                    </Box>
                    <Typography
                      variant="body1_regular"
                      sx={{
                        whiteSpace: 'nowrap',
                        lineHeight: 1
                      }}
                    >
                      {route.name}
                    </Typography>
                  </>
                )}
              </StyledSidebarNavLink>
            </ListItem>
          ))}
        </List>
        {mobile && (
          <MobileLogoutWrapper onClick={handleLogout}>
            <Box>
              <LogOut />
            </Box>
            <Typography
              variant="body1_regular"
              sx={(theme) => ({
                opacity: !isSideNavOpen ? 1 : 0,
                whiteSpace: 'nowrap',
                fontSize: !isSideNavOpen ? 'inherit' : 0,
                lineHeight: 1
              })}
            >
              Logout
            </Typography>
          </MobileLogoutWrapper>
        )}
      </Box>
    </Box>
  )
}
